import QuillDelta from 'quill-delta';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';

import { QuillText } from '../form/controls/QuillText';
import { tokens } from '../theme/tokens';
import { Avatar } from './Avatar';
import { FlexGap } from './FlexGap';
import { Skeleton } from './Skeleton';
import { Stars } from './Stars';
import { Label, Paragraph } from './Text';

const Card = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.inline}px;
	width: 100%;
	box-sizing: border-box;
	padding: ${tokens.padding.control}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.sm}px;
`;

const AvatarLink = styled(Link)`
	display: flex;
	flex-shrink: 0;
`;

const NameLink = styled(Link)`
	color: inherit;
	text-decoration: none;
	min-width: 0;

	&:hover {
		text-decoration: underline;
	}
`;

const Name = styled(Label)`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Body = styled.div`
	width: 100%;
	color: ${tokens.text.primary};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.body};
	letter-spacing: 0.01em;
	font-family: ${tokens.typography.fontFamily};

	.ql-editor,
	.ql-editor p,
	.ql-editor li {
		font-size: inherit;
		color: inherit;
		line-height: inherit;
		font-weight: inherit;
	}
`;

type UserTextBubble = {
	firstName: string;
	lastName: string;
	avatar?:
		| {
				source: {
					url: string;
				};
		  }
		| null
		| undefined;
	id: string;
};

export const TextBubble = Skeleton(
	function TextBubble(
		props: PropsWithChildren<{
			user: UserTextBubble | graphql.FeedbackRecipient;
			text?: QuillDelta;
			stars?: number;
		}>,
	) {
		const name = `${props.user.firstName} ${props.user.lastName}`;
		const userId = 'id' in props.user ? props.user.id : undefined;

		const avatar = <Avatar user={props.user} size={20} flexShrink={0} />;
		const label = <Name>{name}</Name>;

		return (
			<Card>
				<FlexGap gap={tokens.gap.element} alignItems="center" width="100%">
					{userId ? <AvatarLink to={`/users/${userId}`}>{avatar}</AvatarLink> : avatar}
					{userId ? <NameLink to={`/users/${userId}`}>{label}</NameLink> : label}
				</FlexGap>
				{props.text && (
					<Body>
						<QuillText value={props.text} />
					</Body>
				)}
				{typeof props.stars === 'number' && (
					<Box>
						<Stars stars={props.stars} />
					</Box>
				)}
				{props.children}
			</Card>
		);
	},
	function TextBubbleSkeleton() {
		return (
			<Card>
				<FlexGap gap={tokens.gap.element} alignItems="center">
					<Avatar.Skeleton size={20} />
					<Label.Skeleton />
				</FlexGap>
				<Paragraph.Skeleton lines={2} />
			</Card>
		);
	},
);
